/**
 * The conversation: every message of a thread, in the reading pane.
 *
 * THE HOLE THIS FILLS. Grouping shipped on both sides — `?group=thread` collapses
 * a conversation into one row and the row says how many messages are behind it —
 * and opening that row opened the thread's NEWEST message and nothing else.
 * Every other message in the conversation, INCLUDING THE ONE THAT MATCHED THE
 * QUERY, was unreachable: no route, no expander, no second request. A search
 * that finds a message and then cannot show it to you is worse than a search
 * that finds nothing.
 *
 * A SECOND WALK, NOT A SECOND ENDPOINT. The origin already answers this: a
 * `thread:"<id>"` query over `/search`, which resolves the per-principal store
 * the same way every other search does. That is the safest property of the whole
 * feature — a `GET /threads/:id` would be one more route that has to remember to
 * scope itself to the caller's account, and forgetting that is exactly how the
 * `/suggest` isolation bug happened.
 *
 * ONE MESSAGE ON SCREEN IS STILL ONE MESSAGE'S WORTH OF STATE. `MessageBody`
 * below holds the body, the HTML, the fetched image bytes and — the one that
 * matters — the "Show images" decision, per instance. A conversation renders
 * several of them, and each one's answer is about ITS sender only. Hoisting any
 * of that into the conversation would mean pressing "Show images" on a message
 * from somebody you trust silently loads the tracking pixels of the stranger
 * three messages down.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  api,
  // The API's `MessageBody` is the TEXT half of a message; the component below
  // is the whole of one. Aliased so both keep the name that is right for them.
  type MessageBody as MessageText,
  type MessageHtml,
  type MessageRow,
} from './api.ts'
import { HtmlFrame } from './HtmlFrame.tsx'
import { sanitiseHtml } from './sanitise.ts'
import { fileSize, isUnread, listDate, many, recipientLine, senderName } from './format.ts'
import { describeError, fillPage, type ReadingPrefs } from './app.tsx'

/**
 * Is this row a conversation, and if so what addresses it?
 *
 * ONE FUNCTION FOR BOTH QUESTIONS, because they are one question and this repo's
 * commonest bug is two predicates that must agree and do not. The shell asks it
 * to decide what the header says; the pane asks it to decide whether to walk.
 *
 * `thread_count` IS ABSENT ON A MESSAGE PAGE — the message walk does not count
 * threads — so a `group=message` list returns null here and the pane keeps
 * exactly the behaviour it had before this file existed. That is not an
 * optimisation: without it, `j` down a message list would fire a `thread:`
 * search per keystroke.
 *
 * A COUNT OF 1 IS NOT A CONVERSATION EITHER, and that gate is what makes the
 * null `thread_id` below unreachable rather than handled. `thread:` compiles to
 * a bare `thread_id = ?` (compile-sql.ts), which cannot address a message whose
 * thread_id is null — but the aggregate keys such a message by its own guid
 * (store.ts `threadKeyMatches`), and a guid is unique, so a conversation of more
 * than one message always has a real thread id. The alternative — teaching
 * `thread:` a null/guid disjunction — would be that rule stated a second time,
 * in SQL, in another package.
 */
export function conversationKey(row: MessageRow): string | null {
  if (row.thread_count === undefined || row.thread_count <= 1) return null
  return row.thread_id
}

/**
 * The query that walks one conversation.
 *
 * QUOTED, ALWAYS. `lex.ts` reads a bare value up to whitespace or a paren and a
 * quoted one with backslash escapes, and a Message-ID is a string a stranger
 * chose: `<a b@x>` unquoted parses as two terms, and one containing `"` or `\`
 * would parse as something else again. Escaping both characters and quoting is
 * the whole of it.
 *
 * DELIBERATELY NOT NORMALISED. `thread:` does not fold angle brackets or case
 * the way `msgid:` does — a thread id is an opaque grouping key the index minted,
 * not an address — so the id goes to the server exactly as the row carried it.
 */
export function threadQuery(threadId: string): string {
  return `thread:"${threadId.replace(/["\\]/g, '\\$&')}"`
}

/**
 * How much of a very large conversation is fetched.
 *
 * A family thread is a dozen messages; a mailing list one can be thousands, and
 * `fillPage` would walk it in requests of 200 until the tab fell over. The walk
 * is NEWEST FIRST and reversed for display, which is the half of this decision
 * worth writing down: cut at the oldest end and the reader loses history they
 * can still reach by searching, while cutting at the newest end would drop the
 * message they were just looking at in the list. The header says when it has cut.
 */
const MAX_CONVERSATION = 200

/**
 * How many bodies may be open at once.
 *
 * EVERY EXPANDED MESSAGE IS TWO REQUESTS AND AN IFRAME. The expansion rule
 * (newest, plus everything unread) is `shell-ia.md`'s and it is right for a
 * conversation of six; for a conversation of two hundred unread messages it is
 * two hundred bodies, two hundred HTML fetches and two hundred sandboxed
 * documents on one page. The cap keeps the newest ones — the end of the thread
 * is where a reader lands — and every message above it is one keystroke away.
 */
const MAX_OPEN_BODIES = 8

/**
 * Which messages a conversation opens with.
 *
 * `shell-ia.md`: the latest message and every unread one expanded, everything
 * else a stub. Walked from the newest backwards so that when the cap bites it
 * bites the oldest unread, not the newest.
 */
function initialExpansion(messages: readonly MessageRow[]): Set<string> {
  const open = new Set<string>()
  for (let at = messages.length - 1; at >= 0 && open.size < MAX_OPEN_BODIES; at--) {
    const message = messages[at]!
    if (at === messages.length - 1 || isUnread(message.flags)) open.add(message.id)
  }
  return open
}

interface Loaded {
  /** Oldest first, which is how a conversation reads. */
  readonly messages: readonly MessageRow[]
  /** The id the walk was for, so a stale answer cannot be applied to another. */
  readonly key: string
  readonly truncated: boolean
}

export interface ConversationProps {
  /** The row the list is on: a `group=thread` row is the thread's newest message. */
  readonly row: MessageRow
  readonly prefs: ReadingPrefs
  readonly onMarkRead: (row: MessageRow) => void
  /**
   * Bumped when the reader pressed Enter, and by nothing else.
   *
   * A CLICK MUST NOT MOVE FOCUS IN HERE. `useKeyboard`'s context is derived from
   * where focus is, so stealing it on every opened row would put the shell in
   * the `thread` context the moment somebody used the mouse — and `x`, `*` and
   * range selection are `list` bindings. That is the regression app.tsx records
   * against deriving the context from `cursorRow`, wearing different clothes.
   */
  readonly focusSignal: number
  /**
   * Which message the reader is ON, or null when they are not in one.
   *
   * REPLY IS ABOUT A MESSAGE, NOT ABOUT A ROW. `r`, `a` and `f` have always
   * acted on the cursor row, which for a `group=thread` list is the thread's
   * NEWEST message — so without this, opening a four-message conversation,
   * moving to the one that matched the search and pressing `r` would quote and
   * address somebody else's message. The shell keeps the row for everything
   * else, because archiving and starring a conversation from its header really
   * are about the row.
   */
  readonly onFocusedMessage: (row: MessageRow | null) => void
}

export function Conversation({
  row,
  prefs,
  onMarkRead,
  focusSignal,
  onFocusedMessage,
}: ConversationProps): React.JSX.Element {
  const key = conversationKey(row)
  /**
   * ONE MORE THAN THE ROW CLAIMS, up to the ceiling — and the `+ 1` is the whole
   * of the fix it represents.
   *
   * `thread_count` comes from the `thread_aggregate` row on a list page that may
   * be minutes old, and the origin recomputes that count on every ingest. So a
   * conversation the list said held 3 can hold 5 by the time it is opened. Asked
   * for exactly 3, newest first, the walk returned the newest 3 — and the
   * ORIGINAL message, the one the whole thread is a reply to, was not among
   * them, was reported by nothing, and had no expander or route to reach it.
   * That is mail unreachable with no notice, which is the failure this file
   * exists to end rather than to introduce somewhere new.
   *
   * Asking for one more is what makes "there are more than the row said" an
   * observable fact instead of an assumption: if the extra row comes back, the
   * count was stale and the banner says so.
   */
  const want = Math.min((row.thread_count ?? 1) + 1, MAX_CONVERSATION)
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const [focused, setFocused] = useState<string | null>(null)
  const container = useRef<HTMLDivElement>(null)
  const heads = useRef(new Map<string, HTMLButtonElement>())

  /**
   * THE SECOND WALK.
   *
   * KEYED ON THE THREAD, NOT ON THE ROW. Moving the cursor between two messages
   * of the same conversation — which `group=thread` cannot even produce, but a
   * permalink and a message list can — must not refetch it, and the row object
   * is replaced wholesale every time a flag changes.
   *
   * `live` GUARDS THE APPLY, the same way the body fetch below does: a slow walk
   * for the conversation the reader has left must not land on the one they are
   * looking at now.
   */
  useEffect(() => {
    if (key === null) {
      setLoaded(null)
      setError(null)
      return
    }
    let live = true
    setError(null)
    fillPage(threadQuery(key), want, null, {
      group: 'message',
      // NEWEST FIRST AND REVERSED BELOW. See `MAX_CONVERSATION`: it is the only
      // ordering whose truncation loses the oldest rather than the newest.
      order: 'desc',
      // `thread_count` already said how big this is. Counting it again is a
      // second query per conversation opened for a number already on the row.
      count: 'none',
    })
      .then((page) => {
        if (!live) return
        const messages = [...page.rows].reverse()
        setLoaded({
          messages,
          key,
          // TRUNCATED MEANS THE WALK LEFT SOMETHING BEHIND, and there are two
          // ways for that to be true. The obvious one is the ceiling. The other
          // is a cursor: `fillPage` stops when it has `want` rows, so a
          // `nextCursor` means the origin had more to give — which is exactly
          // the case a stale `thread_count` produces, and the case the first
          // version could not see because it only ever compared against the
          // constant.
          //
          // NOT "fewer rows came back than the row's count said", which is the
          // third reading and the wrong one: the aggregate can be AHEAD of the
          // index by a message the mail server has already moved, and announcing
          // that as "showing the newest 3 of 4" is the client inventing a
          // message it then blames itself for hiding.
          truncated: page.cursor !== null || page.rows.length >= MAX_CONVERSATION,
        })
        setExpanded(initialExpansion(messages))
        setFocused(null)
      })
      .catch((problem: unknown) => {
        if (!live) return
        setLoaded(null)
        setError(describeError(problem))
      })
    return () => {
      live = false
    }
    // `want` IS HOW THE COUNT GETS IN, and the row object deliberately is not a
    // dependency: it is replaced wholesale every time a flag changes, so
    // depending on it would re-walk the whole conversation each time the pane
    // marked one of its messages read.
  }, [key, want])

  /**
   * WHERE THE UNREAD PART STARTS, and how much of it there is.
   *
   * DERIVED FROM THE WALK AND FROM NOTHING ELSE, which is what makes the divider
   * "persistent for the session" (`shell-ia.md`) rather than a thing that jumps
   * as the pane marks messages read under the reader. The walked rows are never
   * patched, so this answer does not move while the conversation is open.
   */
  const divider = useMemo(() => {
    const messages = loaded?.messages ?? []
    const at = messages.findIndex((message) => isUnread(message.flags))
    return { at, count: messages.filter((message) => isUnread(message.flags)).length }
  }, [loaded])

  /** Where `n`/`p` start from, and what Enter lands on: the first unread, else the newest. */
  const entry = useMemo(() => {
    const messages = loaded?.messages ?? []
    return messages[divider.at >= 0 ? divider.at : messages.length - 1]?.id ?? null
  }, [loaded, divider.at])

  const focusMessage = useCallback((id: string | null): void => {
    const head = id === null ? null : heads.current.get(id)
    if (head) {
      head.focus()
      // GUARDED. jsdom has no layout and no `scrollIntoView`; commit 974bb06
      // fixed a crash where the console called it unconditionally.
      head.scrollIntoView?.({ block: 'nearest' })
      return
    }
    // Nothing to land on yet — the walk may still be in flight. The container
    // itself takes the focus so the keyboard context is still the thread's.
    container.current?.focus()
  }, [])

  // ENTER PUT THE READER IN HERE. Focus is the whole mechanism: it is what tells
  // the shell to switch the keymap to `thread`, and it is what makes `n` and `p`
  // below reachable at all.
  const landed = useRef(0)
  useEffect(() => {
    if (focusSignal === 0 || focusSignal === landed.current) return
    landed.current = focusSignal
    focusMessage(entry)
  }, [focusSignal, entry, focusMessage])

  // THE FIRST UNREAD, BROUGHT INTO VIEW ON OPEN — `shell-ia.md`'s answer to
  // Gmail's most-cited thread failure, which is landing a reader at the top of a
  // forty-message thread with the new part below the fold. Scroll only; taking
  // focus here would fight the rule above about clicks.
  useEffect(() => {
    if (divider.at < 0 || !loaded) return
    const first = loaded.messages[divider.at]
    if (first) heads.current.get(first.id)?.scrollIntoView?.({ block: 'center' })
  }, [loaded, divider.at])

  /**
   * TOLD TO THE SHELL, so `r` and `a` reach the right message.
   *
   * The row and nothing else while the reader is not on a message — `focused` is
   * set by a header taking focus, so it is null until they are actually in one,
   * and the shell falls back to the cursor row exactly as it always did.
   */
  const focusedRow = useMemo(
    () => loaded?.messages.find((message) => message.id === focused) ?? null,
    [loaded, focused],
  )
  useEffect(() => onFocusedMessage(focusedRow), [focusedRow, onFocusedMessage])

  const toggle = useCallback((id: string): void => {
    setExpanded((open) => {
      const next = new Set(open)
      if (!next.delete(id)) next.add(id)
      return next
    })
  }, [])

  /**
   * `n`, `p`, `o`, `;`, `:` — `interaction.md`'s thread keys.
   *
   * ON THE CONTAINER RATHER THAN ON THE WINDOW, and that is what keeps there
   * from being a second keyboard layer to disagree with `useKeyboard`: these
   * fire only when focus is inside the conversation, so the text-entry
   * suppression, the modal suppression and the Escape ladder all stay stated
   * exactly once, in the file that owns them.
   *
   * `stopPropagation` because the shell's listener is on `window`, above this.
   * None of these five keys is bound in the keymap today, and the day one of
   * them is, the message under the reader's focus must win.
   */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const messages = loaded?.messages ?? []
      const step = (delta: number): void => {
        if (messages.length === 0) return
        const from = messages.findIndex((message) => message.id === (focused ?? entry))
        const to = Math.min(messages.length - 1, Math.max(0, (from < 0 ? 0 : from) + delta))
        const next = messages[to]
        if (!next) return
        setFocused(next.id)
        focusMessage(next.id)
      }
      switch (event.key) {
        case 'n':
          step(1)
          break
        case 'p':
          step(-1)
          break
        case 'o': {
          const id = focused ?? entry
          if (id) toggle(id)
          break
        }
        case ';':
          setExpanded(new Set(messages.map((message) => message.id)))
          break
        case ':':
          // COLLAPSE ALL KEEPS ONE OPEN, deliberately: a pane showing nothing but
          // stubs is a reading pane with no message in it, which is not a state
          // any key asked for.
          setExpanded(new Set(entry ? [entry] : []))
          break
        default:
          return
      }
      event.preventDefault()
      event.stopPropagation()
    },
    [loaded, focused, entry, focusMessage, toggle],
  )

  /**
   * THE GATE, AND IT COMES FIRST.
   *
   * A row that is not a conversation is rendered exactly as it always was: one
   * body, no second request. This is the branch that keeps a message list cheap.
   */
  if (key === null) {
    // `data-conversation` IS THE SAME GATE, HANDED TO THE STYLESHEET. One
    // message fills the pane and the frame scrolls inside it; several must
    // scroll as one column with each frame given a height, because an
    // opaque-origin iframe cannot size itself (see `HtmlFrame`). Two layouts,
    // one predicate, and the CSS reads it rather than guessing from a child.
    return (
      <div
        className="thread"
        data-conversation="false"
        data-thread-focusable=""
        ref={container}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <MessageBody row={row} prefs={prefs} onMarkRead={onMarkRead} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="state" data-kind="error">
        <span className="state-title">This conversation could not be opened</span>
        <span>{error}</span>
      </div>
    )
  }
  if (!loaded || loaded.key !== key) return <div className="spinner">Reading…</div>

  return (
    <div
      className="thread"
      data-conversation="true"
      // THE MARKER THE SHELL READS to decide it is in the `thread` keymap
      // context. It is on the conversation and on each message head, and
      // deliberately NOT on the reading pane's toolbar: focusin bubbles, so a
      // marker further out would make clicking Archive put the shell in a
      // context where `x` and Enter are dead.
      data-thread-focusable=""
      ref={container}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      role="list"
      aria-label={`${many(loaded.messages.length, 'message')} in this conversation`}
    >
      {loaded.truncated ? (
        <p className="thread-truncated" role="status">
          Showing the newest {many(loaded.messages.length, 'message')} in this conversation. There are older ones.
        </p>
      ) : null}
      {loaded.messages.map((message, at) => {
        const open = expanded.has(message.id)
        return (
          <div key={message.id} className="thread-item" role="listitem" data-unread={isUnread(message.flags)}>
            {/* THE DIVIDER SITS BEFORE THE FIRST UNREAD MESSAGE, full width and
                labelled. `aria-hidden` on the rule itself would hide the one
                sentence that explains why the pane scrolled to the middle. */}
            {at === divider.at ? (
              /* NO `role="separator"`, deliberately: a separator's own text is
                 not reliably announced, and the whole value of this line is the
                 sentence in it. It is a paragraph that looks like a rule. */
              <p className="thread-divider">{many(divider.count, 'new message')}</p>
            ) : null}
            <button
              type="button"
              className="thread-head"
              data-thread-focusable=""
              aria-expanded={open}
              ref={(node) => {
                if (node) heads.current.set(message.id, node)
                else heads.current.delete(message.id)
              }}
              onFocus={() => setFocused(message.id)}
              onClick={() => toggle(message.id)}
            >
              <span className="thread-from">{senderName(message.from_addr_name)}</span>
              {/* THE PREVIEW IS WHAT A STUB IS FOR. Expanded, the body is right
                  there and a snippet of it above the message is noise. */}
              {!open && message.preview ? <span className="thread-snippet">{message.preview}</span> : null}
              <span className="thread-when">{listDate(message.date)}</span>
            </button>
            {open ? (
              <div className="thread-open">
                {message.to_addrs ? (
                  <div className="read-meta read-to">To: {recipientLine(message.to_addrs)}</div>
                ) : null}
                {/* NO `key` HERE, ON PURPOSE. The per-message resets inside
                    `MessageBody` are keyed on the message's id and are the one
                    mechanism that clears one sender's images before the next
                    sender's markup is rendered; a `key` would be a second one,
                    and the security rule would then depend on which of the two a
                    later edit kept. The list item above already keys the row. */}
                <MessageBody row={message} prefs={prefs} onMarkRead={onMarkRead} />
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/**
 * `remote_images: 'contacts'` — the registry's own default — behaves as
 * `'never'` here, on purpose.
 *
 * THERE IS NO CLIENT-SIDE "IS THIS SENDER A CONTACT" SIGNAL. The nearest thing
 * is the Screener's admitted-sender set, and treating "allowed past the
 * Screener" as "trusted to learn the moment I opened this" is a privacy
 * decision for the household to take, not one to infer here. Until it is taken,
 * the safe reading of an ambiguous setting is the one that loads nothing:
 * quietly resolving it the other way would opt every reader into tracking
 * pixels by default.
 */
const imagesOnByDefault = (prefs: ReadingPrefs): boolean => prefs.remoteImages === 'always'

/**
 * The ceilings on what the reading pane will put in front of a message.
 *
 * EVERY IMAGE THE FRAME SHOWS IS CARRIED IN THE `srcdoc` STRING, base64 and
 * all, because the frame cannot fetch anything for itself — see `HtmlFrame`.
 * That is the right trade for logos and photographs and the wrong one for a
 * message somebody built out of forty full-size JPEGs, which would be a
 * fifty-megabyte string handed to the parser in one go. Base64 costs a third on
 * top, so the real document is these numbers plus a bit.
 *
 * THE EDGE HAS A CEILING OF ITS OWN and these are not derived from it: they are
 * two independent refusals, in two processes that share no module, and neither
 * needs the other's number to be right. Whichever is lower simply wins, and the
 * only consequence of a mismatch is a fetch that was never going to be shown.
 * The total is the one this side has to add, because the Worker sees one image
 * at a time and never the document they all end up in.
 *
 * PER MESSAGE, AND IN A CONVERSATION THAT NOW MEANS PER MESSAGE ON SCREEN.
 * `MAX_OPEN_BODIES` is what bounds the total: eight bodies at this ceiling
 * each, rather than a thread's worth.
 */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024
const MAX_MESSAGE_IMAGE_BYTES = 16 * 1024 * 1024
/**
 * How many third-party images one message may cost.
 *
 * A newsletter has a dozen. A tracking farm has one per link, each a distinct
 * URL, and pressing "Show images" on it must not become four hundred requests.
 * The rest keep their alt text and are counted as unfetched, which is exactly
 * what they are.
 */
const MAX_REMOTE_IMAGES = 50

/**
 * Where the edge fetches a remote image on the reader's behalf.
 *
 * NOT UNDER `${API}` and so not in `api.ts`: `/_hx/img/` is its own route kind
 * at the edge (`IMG_PREFIX`), classified and gated before any API router sees
 * it, and it answers with bytes rather than JSON.
 */
const imageProxyUrl = (address: string): string => `/_hx/img/?url=${encodeURIComponent(address)}`

/**
 * Bytes, as a `data:` URL the frame can show — or null, meaning it cannot be
 * shown and the reader gets the alt text.
 *
 * NULL FOR EVERY FAILURE, deliberately flattening a 404, a timeout, a refused
 * content type and a broken connection into one outcome. There is exactly one
 * thing the pane can do about any of them, and telling a reader which of four
 * ways a spacer GIF failed is noise.
 */
async function inlineImage(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { headers: { accept: 'image/*' } })
    if (!response.ok) return null
    const type = (response.headers.get('content-type') ?? '').split(';')[0]!.trim().toLowerCase()
    // THE THIRD GATE ON THE SAME QUESTION, and cheap enough to be worth having:
    // the parts route relabels anything outside its allowlist as
    // `application/octet-stream`, the image proxy refuses a non-image outright,
    // and `sanitiseHtml` refuses anything that is not `data:image/…;base64`.
    // This one is what turns a refusal into a null instead of a broken `src`.
    if (!type.startsWith('image/')) return null
    const bytes = new Uint8Array(await response.arrayBuffer())
    if (bytes.byteLength > MAX_IMAGE_BYTES) return null
    return `data:${type};base64,${base64(bytes)}`
  } catch {
    return null
  }
}

/**
 * IN CHUNKS, because `String.fromCharCode(...bytes)` passes one argument per
 * byte and a megabyte of them overflows the call stack — a crash that only
 * happens on the big attachment somebody actually cared about.
 */
function base64(bytes: Uint8Array): string {
  let binary = ''
  const CHUNK = 8192
  for (let at = 0; at < bytes.length; at += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK))
  }
  return btoa(binary)
}

/**
 * Coalesce a burst of updates into one render, and never lose the last one.
 *
 * WHY THE READING PANE NEEDS THIS AND NOTHING ELSE DOES: assigning `srcdoc`
 * RE-NAVIGATES an iframe. Every state write in an image loop re-runs
 * `sanitiseHtml` over the whole message, produces a new `srcdoc` string, and
 * throws the reader's scroll position inside the frame back to the top — so a
 * twenty-image newsletter reloaded its own document twenty times as the pictures
 * arrived. The behaviour worth keeping is that the message fills in as they
 * land; what is not worth keeping is doing it once per image.
 *
 * `flush()` IS PART OF THE CONTRACT. A trailing timer that the last image never
 * outlives would leave the final picture unrendered until something else
 * happened to re-render — which is the same never-finishing state, wearing the
 * clothes of a fix.
 *
 * THERE IS DELIBERATELY NO UNIT TEST FOR THE RE-NAVIGATION ITSELF, and saying so
 * is better than the test that was written for it. jsdom has no layout, no
 * navigation and no scroll position, and React sets `srcDoc` as a PROPERTY — so
 * a MutationObserver on the attribute counts zero writes whether the loop
 * batches or not, and a sampling loop cannot see eight fetches that resolve
 * inside one poll. Both versions passed identically against the un-batched
 * build, which makes them decoration. What IS tested, and what batching must not
 * break, is that every image still arrives: `shows an inline part as bytes`
 * fails if a batch is dropped or applied empty — which is exactly the bug the
 * first attempt at this hook shipped.
 */
function useCoalesced(apply: () => void, ms = 120): { (): void; flush: () => void } {
  const latest = useRef(apply)
  latest.current = apply
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flush = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
    latest.current()
  }, [])

  const schedule = useCallback(() => {
    if (timer.current !== null) return
    timer.current = setTimeout(() => {
      timer.current = null
      latest.current()
    }, ms)
  }, [ms])

  useEffect(() => () => {
    if (timer.current !== null) clearTimeout(timer.current)
  }, [])

  return Object.assign(schedule, { flush })
}

/**
 * The BYTES a `data:` URL carries, not the length of the string.
 *
 * Base64 is 4 characters per 3 bytes, after a `data:image/png;base64,` header
 * that is not payload at all. Spending the string length against a budget
 * declared in bytes overcharges by about 37%, and it was being compared on the
 * same line against a real byte count from BODYSTRUCTURE.
 */
function dataUrlBytes(dataUrl: string | null): number {
  if (!dataUrl) return 0
  const comma = dataUrl.indexOf(',')
  if (comma < 0) return 0
  return Math.floor(((dataUrl.length - comma - 1) * 3) / 4)
}

/**
 * One message's body: the text, the rich half, its images, its attachments, and
 * the decision to show remote images — all of it scoped to THIS message.
 *
 * EXTRACTED FROM THE READING PANE RATHER THAN REWRITTEN. Every effect below kept
 * the key it had, because each of those keys is a fixed bug: `rowId` and not the
 * row object (a flag change replaced the object and re-fetched the body), `body`
 * and not `rowId` for the inline parts (the new message would be asked for the
 * old one's part numbers), `[rowId, prefs]` for the image default (a privacy
 * setting that only applied from the NEXT message is the wrong way round).
 *
 * IN A CONVERSATION THERE ARE SEVERAL OF THESE ON SCREEN AT ONCE, which is what
 * those keys now have to survive: every one of them names the message, never the
 * pane, so eight open messages are eight independent budgets, eight independent
 * "Show images" answers and eight independent mark-read timers.
 */
export function MessageBody({
  row,
  prefs,
  onMarkRead,
}: {
  row: MessageRow
  prefs: ReadingPrefs
  onMarkRead: (row: MessageRow) => void
}): React.JSX.Element {
  const [body, setBody] = useState<MessageText | null>(null)
  const [html, setHtml] = useState<MessageHtml | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // Per message, and reset with it: "show images" is a decision about ONE
  // sender's mail, and carrying it to the next message would silently opt the
  // reader into being tracked by somebody they have not looked at yet. It
  // resets to the ACCOUNT's answer rather than to `false`, which is the same
  // rule said once instead of twice.
  const [showImages, setShowImages] = useState(() => imagesOnByDefault(prefs))
  /**
   * The bytes of every image this message shows, keyed by what asked for it:
   * Content-ID for the message's own parts, the URL itself for a remote one.
   * `null` means "tried, cannot be shown".
   *
   * BYTES AND NOT URLS, AND THAT IS THE WHOLE FIX. The frame has an opaque
   * origin, so a `src` pointing at this application is a cross-site request
   * without our Access session on it, and a `src` pointing at the sender is
   * refused by the frame's `default-src 'none'`. `HtmlFrame` has the long
   * version. What the pane can do — and the frame cannot — is fetch, so it
   * fetches and hands over the result inline.
   */
  const [inlineParts, setInlineParts] = useState<ReadonlyMap<string, string | null>>(() => new Map())
  /**
   * ONE BYTE BUDGET FOR THE MESSAGE, and one place each loop spends it.
   *
   * The two loops each held their own, so a message could carry twice
   * `MAX_MESSAGE_IMAGE_BYTES` — the constant whose own comment says "per
   * message" — into the srcdoc. A ref rather than state because spending it
   * must not re-render anything.
   */
  const imageBudget = useRef(MAX_MESSAGE_IMAGE_BYTES)
  /** Inline answers not yet handed to React. See `flushInline`. */
  const pendingInline = useRef(new Map<string, string | null>())
  const flushInline = useCoalesced(() => {
    // THE BATCH IS TAKEN BEFORE THE UPDATER IS HANDED OVER, and this is not
    // style. React calls a functional update LATER — at render, possibly twice —
    // so an updater that reads `pendingInline.current` reads it as it is THEN,
    // and the line clearing the ref runs first. The first version did exactly
    // that: every batch applied an empty map, and the pictures never appeared.
    const batch = pendingInline.current
    if (batch.size === 0) return
    pendingInline.current = new Map()
    setInlineParts((previous) => {
      const next = new Map(previous)
      for (const [id, value] of batch) next.set(id, value)
      return next
    })
  })
  const [remoteBytes, setRemoteBytes] = useState<ReadonlyMap<string, string | null>>(() => new Map())
  /**
   * The same remote answers, reachable from inside the fetch loop.
   *
   * A MIRROR AND NOT A SECOND TRUTH: the loop must know what it has already
   * asked for, and reading that from state would either capture a stale map or
   * make the effect depend on what the effect writes. "Hide images" then "Show
   * images" is the case this pays for — without it, every hidden-and-shown
   * message hits the sender's server a second time.
   */
  const fetchedRemote = useRef<Map<string, string | null>>(new Map())
  const flushRemote = useCoalesced(() => setRemoteBytes(new Map(fetchedRemote.current)))

  /**
   * Is anybody actually looking?
   *
   * A BACKGROUND TAB MUST NOT CONSUME MAIL. The pane keeps rendering whatever
   * the cursor is on when the reader switches to another tab, and a timer that
   * did not care would mark that message read on the real server — in
   * Thunderbird and on their phone too — for a message nobody saw.
   */
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden')
  /** The last message this instance has already tried to mark read. See the timer. */
  const attempted = useRef<string | null>(null)
  /**
   * The row and the callback, read through refs at the moment the timer fires.
   *
   * SAME REASON `useKeyboard` READS ITS HANDLER THROUGH ONE, and here it is the
   * difference between the feature working and not: `useOps` returns a fresh
   * object on every render of the shell, so a timer whose effect depended on the
   * callback's identity is cleared and re-armed on EVERY render — and a shell
   * that re-renders every second or so (a reconnecting socket, a Screener
   * refresh, a page landing) never reaches two seconds. Measured: the timer was
   * disarmed at 751 ms, 1307 ms and 1957 ms and never fired once.
   */
  const rowRef = useRef(row)
  rowRef.current = row
  const markReadRef = useRef(onMarkRead)
  markReadRef.current = onMarkRead
  useEffect(() => {
    const onChange = (): void => setVisible(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])

  // KEYED ON THE MESSAGE'S ID, NOT ON THE ROW OBJECT. The row is replaced by a
  // new object every time its flags change — which auto mark-read now does to
  // the message being read — and keying on identity would re-fetch the body and
  // flash "Reading…" the instant it was marked read.
  const rowId = row.id
  const unread = isUnread(row.flags)
  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    setHtml(null)
    api
      .body(rowId)
      .then((next) => {
        if (live) setBody(next)
      })
      .catch((e: unknown) => {
        if (live) setError(describeError(e))
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [rowId])

  /**
   * THE REMOTE-IMAGE DEFAULT IS ITS OWN EFFECT, and not a line inside the body
   * fetch above.
   *
   * It was one, keyed on `rowId` alone while reading `prefs` — inert only for
   * as long as `readingPrefs` was a frozen constant. The moment the account's
   * own settings are read back (they are, now, one request on mount), a
   * household that had chosen `always` would have got the registry's `never`
   * on the message already open, and turning remote images off would not have
   * taken effect until the reader moved to a different message. A privacy
   * setting that applies from the NEXT message onwards is the wrong way round.
   *
   * Keyed on both, so it re-seeds when either the message or the preference
   * changes — which is also what makes the reset a security rule rather than a
   * convenience: one "Show images" must never opt the reader into the next
   * sender's tracking.
   */
  useEffect(() => {
    setShowImages(imagesOnByDefault(prefs))
  }, [rowId, prefs])

  /**
   * FETCHED BYTES ARE PER MESSAGE TOO, and for the same reason `showImages` is.
   *
   * They are also the mechanism: a map left over from the last message would
   * have the pane rendering one sender's pictures inside another sender's mail
   * — the "show images" reset would still say the right thing and the frame
   * would still be full of images. Declared BEFORE the two effects that fill
   * these, so on a message change the clear happens first.
   */
  useEffect(() => {
    fetchedRemote.current = new Map()
    pendingInline.current = new Map()
    imageBudget.current = MAX_MESSAGE_IMAGE_BYTES
    setRemoteBytes(new Map())
    setInlineParts(new Map())
  }, [rowId])

  /**
   * The message's own inline parts, fetched and inlined.
   *
   * KEYED ON THE BODY AND NOT ON `rowId`, which is not a detail: `body` is left
   * standing while the next message's fetch is in flight, so an effect keyed on
   * the row would ask the NEW message for the OLD message's part numbers and
   * get a wall of 404s.
   *
   * EVERY PART WITH A CONTENT-ID AND AN IMAGE TYPE, rather than only the ones
   * the markup turns out to reference. A Content-ID exists because something
   * points at it; filtering first would mean sanitising once to find out,
   * fetching, and sanitising again. `inline` is deliberately NOT the test here
   * — a Content-ID in a `multipart/mixed` is still what `cid:` resolves to —
   * and it IS the test for the attachment list further down, which asks a
   * different question.
   */
  useEffect(() => {
    const guid = body?.id
    // NOTHING TO SHOW THEM IN. `cid:` only ever appears in the HTML half, so a
    // message without one has no reference to resolve and these bytes would be
    // fetched for nobody.
    if (!guid || !body.hasHtml) return
    const wanted = body.attachments.flatMap((part) =>
      part.contentId && part.type.toLowerCase().startsWith('image/')
        ? [{ id: part.contentId.toLowerCase(), section: part.part, size: part.size }]
        : [],
    )
    if (wanted.length === 0) return
    let live = true
    void (async () => {
      // ONE AT A TIME, and the message fills in as they land. A newsletter's
      // logos are small and sequential costs little; what it buys is a running
      // total that can actually stop, and a reading pane that never has twenty
      // requests of its own in flight while the reader is trying to scroll.
      for (const part of wanted) {
        if (!live) return
        // The DECLARED size — BODYSTRUCTURE's number, so the sender's. Used only
        // to skip a fetch that is obviously too big; what arrives is measured.
        const inlined =
          part.size > MAX_IMAGE_BYTES || part.size > imageBudget.current
            ? null
            : await inlineImage(api.partUrl(guid, part.section))
        if (!live) return
        imageBudget.current -= dataUrlBytes(inlined)
        pendingInline.current.set(part.id, inlined)
        flushInline()
      }
      flushInline.flush()
    })()
    return () => {
      live = false
    }
  }, [body])

  // THE RICH HALF IS A SECOND REQUEST, and only when there is one to ask for.
  // `hasHtml` comes from the text response, so this cannot run until that has
  // landed — which is also why the text appears first and is replaced, rather
  // than the pane sitting empty until the HTML arrives.
  useEffect(() => {
    if (!body?.hasHtml) return
    let live = true
    api
      .html(rowId)
      .then((next) => {
        if (live) setHtml(next)
      })
      .catch(() => {
        // A failed rich render is not a failed message: the text is already on
        // screen and stays there. Silent on purpose.
      })
    return () => {
      live = false
    }
  }, [rowId, body?.hasHtml])

  /**
   * MARK IT READ. Nothing in this client ever did, except `Shift+I` pressed by
   * hand — so a household using Hearth had an Inbox where every message stayed
   * bold for ever, on every device that reads the same IMAP account.
   *
   * NOT GATED ON THE BODY HAVING ARRIVED, and that is a decision rather than an
   * oversight: the setting is about the message being ON SCREEN for a while,
   * and gating it on the fetch would leave a slow message silently unread while
   * the reader sat looking at it.
   *
   * IN A CONVERSATION, ON SCREEN MEANS EXPANDED. A collapsed message does not
   * render this component at all, so a stub cannot be marked read by having been
   * scrolled past — which is the behaviour a reader expects and also the only
   * one a `\Seen` write to a real mail server can be defended as.
   *
   * KEYED ON `rowId` WITH THE TIMER CLEARED ON THE WAY OUT, which is the whole
   * safety story for `j j j`: moving the cursor down a list cancels each timer
   * before it fires, so a fast scroll consumes nothing. The other half is
   * `visible` above.
   *
   * `-1` NEVER, `0` ON OPEN. Both go through the same timer so there is one
   * cleanup path rather than a special case that can leak.
   */
  useEffect(() => {
    // ALREADY READ SENDS NOTHING. A `+FLAGS (\Seen)` per cursor rest is an IMAP
    // write per keystroke for a change the server has already made.
    if (!unread) return
    if (prefs.markReadAfterMs < 0 || !visible) return
    // ONCE PER MESSAGE, AND THIS IS NOT BELT AND BRACES. A refusal at the mail
    // server rolls the row's flags BACK to unread, which would re-satisfy every
    // condition above and re-arm the timer — a write the server rejects, retried
    // every two seconds for as long as the reader leaves the message open.
    // It also settles `Shift+U`: marking the open message unread on purpose
    // stays unread, instead of being undone two seconds later by the pane.
    if (attempted.current === rowId) return
    const timer = setTimeout(() => {
      // RE-CHECKED AT THE MOMENT IT FIRES, because the two seconds are two
      // seconds of a live list: `Shift+I` or another device may already have
      // done it, and a redundant `+FLAGS` is an IMAP write for no change.
      const live = rowRef.current
      if (live.id !== rowId || !isUnread(live.flags)) return
      attempted.current = rowId
      markReadRef.current(live)
    }, prefs.markReadAfterMs)
    return () => clearTimeout(timer)
    // DEPS ARE THE FACTS, NOT THE OBJECTS. `unread` rather than `row`: the row
    // is replaced wholesale whenever any flag changes, and re-arming on a star
    // or a label would restart the clock on a message the reader has been
    // looking at for a minute.
  }, [rowId, unread, prefs.markReadAfterMs, visible])

  /**
   * FILTERED HERE, IN THE BROWSER'S OWN PARSER, and re-run when the reader asks
   * for images. `html.html` is exactly what the sender wrote — the origin does
   * not touch it — so this is the only thing between a stranger's markup and
   * the frame, and it uses `DOMParser` rather than anything hand-written.
   *
   * `cid:` resolves against the parts fetched above, so what the filter emits
   * is bytes rather than a URL — the frame it is going into cannot make a
   * request, to the sender or to us.
   */
  const cleaned = useMemo(() => {
    if (!html?.html) return null
    return sanitiseHtml(html.html, {
      resolveCid: (id) => inlineParts.get(id.toLowerCase()) ?? null,
      // ONLY WHEN THE READER ASKED, and passing nothing is how "no" is said.
      // The bytes are NOT dropped when they say no again: "Hide images" is then
      // instant and costs the sender's server nothing to undo.
      resolveRemote: showImages ? (address) => remoteBytes.get(address) ?? null : undefined,
    })
  }, [html?.html, inlineParts, remoteBytes, showImages])

  /**
   * The remote images the reader asked for, fetched THROUGH THE EDGE.
   *
   * `imageProxyUrl` is the whole privacy story: the sender's server is
   * contacted by Cloudflare, not by the reader's browser, with none of their
   * headers and none of their cookies. What the sender still learns is that the
   * message was opened — the URL usually names the recipient — and that is the
   * cost the banner states before the button is pressed.
   *
   * KEYED ON THE JOINED LIST, NOT THE ARRAY. `cleaned` is rebuilt on every
   * arrival, so a new array identity each time would re-arm this effect for
   * ever. The URLs themselves do not change as they resolve, which is why
   * `sanitiseHtml` reports them whether or not it showed them.
   */
  const remoteKey = cleaned?.remoteImageUrls.join('\n') ?? ''
  useEffect(() => {
    if (!showImages || remoteKey === '') return
    let live = true
    void (async () => {
      // ONE BUDGET FOR THE MESSAGE, held in a ref so the inline loop and this
      // one spend the SAME 16 MB. They each had their own, so a message could
      // carry twice the constant's own documented ceiling into the srcdoc.
      for (const [index, address] of remoteKey.split('\n').entries()) {
        if (!live) return
        if (fetchedRemote.current.has(address)) continue
        const inlined =
          index >= MAX_REMOTE_IMAGES || imageBudget.current <= 0
            ? null
            : await inlineImage(imageProxyUrl(address))
        if (!live) return
        // MEASURED IN BYTES, WHICH IS WHAT THE CONSTANT IS IN. This spent
        // `inlined.length` — the length of the base64 `data:` STRING, about
        // 1.37× the bytes plus a header — while the skip above compares the
        // same budget against a declared byte count. Two units on one line.
        imageBudget.current -= dataUrlBytes(inlined)
        fetchedRemote.current.set(address, inlined)
        // BATCHED, because each write re-runs `sanitiseHtml` over the whole
        // message and produces a new `srcdoc` — and ASSIGNING `srcdoc`
        // RE-NAVIGATES THE IFRAME. A twenty-image newsletter reloaded its own
        // document twenty times as the pictures arrived, throwing the reader's
        // scroll position back to the top on each one.
        flushRemote()
      }
      flushRemote.flush()
    })()
    return () => {
      live = false
    }
  }, [rowId, showImages, remoteKey])

  /**
   * What the banner is allowed to say, derived rather than tracked.
   *
   * A SEPARATE `loading` FLAG WOULD BE A SECOND OPINION ABOUT THE SAME FACTS,
   * and the version this replaces had one: the button read "Loading…", stayed
   * disabled for ever, and nothing on screen ever contradicted it because
   * nothing ever finished. An image is outstanding when the map has no answer
   * for it yet — that falls out of the map.
   *
   * HOW MANY DID NOT MAKE IT is NOT counted here, deliberately. It is
   * `blockedRemoteImages`, which the filter already knows: it counts every
   * image it did not show, whether the fetch failed or the address was never
   * fetchable in the first place (a protocol-relative or a relative `src` has
   * no origin to ask). Counting the nulls instead would quietly claim success
   * for the second kind.
   */
  const remoteUrls = cleaned?.remoteImageUrls ?? []
  const awaiting = showImages ? remoteUrls.filter((address) => !remoteBytes.has(address)).length : 0
  /**
   * INLINE PARTS ARE NOT FILES. `AttachmentPart.inline` is the flag core
   * computes for exactly this — a `multipart/related` resource with a
   * Content-ID, which is to say one of the pictures the frame has just shown —
   * and nothing in this client had ever read it. Every logo, spacer and
   * signature image appeared a second time as a download chip: for a typical
   * newsletter, a wall of `image001.png · 4 kB` under the message.
   *
   * "WAS THIS PART ACTUALLY RENDERED", NOT "IS THE FRAME ON SCREEN". The first
   * version asked the second question, and the difference is mail becoming
   * unreachable — this repo's cardinal sin, and the exact failure the hand-
   * written sanitiser was deleted for. Three reachable paths where an
   * inline-flagged part is NOT displayed and would have been hidden anyway: a
   * fetch that returned null (over the per-image cap, out of the message
   * budget, or the network failed); a `multipart/related` sibling that is not an
   * image at all, which the fetch loop skips and the frame never shows; and an
   * image type the origin relabels `application/octet-stream`, which the frame
   * then refuses. In every one of those the download chip is the only way to
   * reach the file.
   *
   * KEYED ON THE CONTENT-ID, which is what `inlineParts` is keyed on and what
   * `cid:` resolves against — not on the part section, which is a different
   * identifier for the same thing.
   */
  const rendered = (part: { inline: boolean; contentId: string | null }): boolean =>
    part.inline &&
    part.contentId !== null &&
    (inlineParts.get(part.contentId.toLowerCase()) ?? null) !== null
  const attachments =
    body && cleaned?.html ? body.attachments.filter((part) => !rendered(part)) : (body?.attachments ?? [])

  if (error) {
    return (
      <div className="state" data-kind="error">
        <span className="state-title">This message could not be read</span>
        <span>{error}</span>
      </div>
    )
  }
  if (loading || !body) return <div className="spinner">Reading…</div>

  return (
    <>
      {cleaned && cleaned.html ? (
        <div className="read-rich">
          {cleaned.blockedRemoteImages > 0 || remoteUrls.length > 0 ? (
            <div className="images-blocked">
              {/* WHAT IT COSTS, SAID BEFORE THE BUTTON IS PRESSED and not
                  after. The old copy — "loading them tells the sender you
                  opened this" — was true of a browser that fetches them
                  itself, which is exactly what no longer happens: the edge
                  fetches, so the request carries no address of theirs and
                  none of their headers. What survives is real and stated:
                  the sender learns the message was opened, because the URL
                  they wrote usually names the recipient. */}
              <span>
                {!showImages
                  ? `${many(cleaned.blockedRemoteImages, 'remote image')} blocked. Loading them tells the sender the message was opened — Hearth fetches them, so they never see your address or your browser.`
                  : awaiting > 0
                    ? `Fetching ${many(awaiting, 'image')}…`
                    : cleaned.blockedRemoteImages > 0
                      ? `${many(cleaned.blockedRemoteImages, 'image')} could not be shown.`
                      : 'Images fetched by Hearth, not by your browser.'}
              </span>
              {/* A DELIBERATE ACT, and only for this message — unless the
                  account has said `never`, in which case offering the button
                  would be offering to overrule a setting they wrote down.
                  IT GOES BOTH WAYS. There was no way back: the button
                  disabled itself on the first press and the banner unmounted
                  under it, so a reader who pressed it by accident had no
                  route to "actually, don't".
                  ITS NAME IS ITS LABEL AND NOTHING MORE, even though a
                  conversation can have several of these on screen: the message
                  it belongs to is the `listitem` around it, which is where a
                  screen reader takes the context from and where a test scopes
                  its query. Baking the sender into the button's name instead
                  would state the sender twice on one line. */}
              {prefs.remoteImages === 'never' && !showImages ? null : (
                <button type="button" onClick={() => setShowImages(!showImages)}>
                  {showImages ? 'Hide images' : 'Show images'}
                </button>
              )}
            </div>
          ) : null}
          <HtmlFrame html={cleaned.html} title={`Message: ${row.subject ?? '(no subject)'}`} />
        </div>
      ) : (
        <div className="read-plain">
          <div className="read-body">
            {body.text}
            {body.truncated ? '\n\n[…this message is longer than the reading limit]' : ''}
          </div>
          {body.quoted ? (
            <details
              className="read-quoted"
              // KEYED ON THE MESSAGE, AND `open` IS AN INITIAL ATTRIBUTE
              // RATHER THAN A CONTROLLED PROP. `<details>` toggles itself;
              // driving `open` from a prop every render would spring it back
              // open the instant the reader collapsed it. Re-keying per
              // message is what makes the account's answer apply again to
              // the next one.
              key={rowId}
              open={prefs.quotedText === 'show'}
            >
              <summary>Show quoted text</summary>
              <div className="read-body read-quoted-text">{body.quoted}</div>
            </details>
          ) : null}
        </div>
      )}
      {attachments.length > 0 ? (
        <div className="attachments">
          {attachments.map((attachment) => (
            <a
              key={attachment.part}
              className="attachment"
              href={api.partUrl(row.id, attachment.part)}
              target="_blank"
              rel="noreferrer"
            >
              <span>{attachment.filename ?? attachment.type}</span>
              <span style={{ color: 'var(--text-faint)' }}>{fileSize(attachment.size)}</span>
            </a>
          ))}
        </div>
      ) : null}
    </>
  )
}
